import {
  HelpOutlineIcon,
  InfoOutlineIcon,
  PhoneCallsIcon,
  PrivacyOutlineIcon,
} from "@tandemdiabetes/sugarmate-react-components"
import dayjs from "dayjs"
import type {
  MouseEventHandler,
  FunctionComponent,
  SVGProps,
} from "react"
import {
  useCallback,
  useEffect,
} from "react"

import styles from "./HelpAndFeedbackIndex.module.scss"
import {
  PageTemplate,
  PageLink,
  PageLinkButton,
} from "src/components"
import { usePolyglot } from "src/contexts"
import { useSegment } from "src/contexts/SegmentContext"
import { useAccount } from "src/hooks"
import {
  getDeviceRegion,
  isUS,
} from "src/utils"

// note we are checking device region not account region
const isUSDevice = isUS(getDeviceRegion())

export function HelpAndFeedbackIndex() {
  const polyglot = usePolyglot()
  const analyticsRef = useSegment()
  const { data: account } = useAccount()

  useEffect(
    () => {
      window.Intercom(
        "boot",
        {
          app_id: process.env.REACT_APP_INTERCOM_APP_ID ?? "",
          user_id: account ? `${account.id}` : undefined,
          email: account?.email,
          name: account?.nickname || account?.firstname || undefined,
          user_hash: account?.intercom_user_hash,
          created_at: account ? dayjs(account.created_at).unix() : undefined,
        },
      )

      // we shut down intercom when we navigate away from this page
      // otherwise, the intercom launcher may obfuscate the FAB on the home page
      return () => {
        window.Intercom("shutdown")
      }
    },
    [account],
  )

  const onContactClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    () => {
      window.Intercom("show")

      void analyticsRef.current.track(
        "Click Button",
        {
          "Button Name": "Contact",
          Path: window.location.pathname,
        },
      )
    },
    [analyticsRef],
  )

  return (
    <PageTemplate
      user="account"
      title={polyglot.t("components.page_header.pages.help_and_feedback")}
    >
      <div className={styles.container}>
        <PageLink
          routePath={"/help_and_feedback/help" as const}
          id="Help"
          polyglotPhraseKey={"pages.help_and_feedback.pages.help"}
          Icon={HelpOutlineIcon as FunctionComponent<SVGProps<SVGElement>>}
        />
        <PageLinkButton
          onClick={onContactClick}
          id="Contact"
          polyglotPhraseKey={"pages.help_and_feedback.pages.contact"}
          Icon={PhoneCallsIcon as FunctionComponent<SVGProps<SVGElement>>}
        />
        <PageLink
          routePath={"/help_and_feedback/about" as const}
          id="About"
          polyglotPhraseKey={"pages.help_and_feedback.pages.about"}
          Icon={InfoOutlineIcon as FunctionComponent<SVGProps<SVGElement>>}
        />
        {!isUSDevice && (
          <PageLink
            routePath={"/help_and_feedback/privacy" as const}
            id="Privacy"
            polyglotPhraseKey={"pages.help_and_feedback.pages.privacy_and_data_use"}
            Icon={PrivacyOutlineIcon as FunctionComponent<SVGProps<SVGElement>>}
          />
        )}
      </div>
    </PageTemplate>
  )
}
